"use client";

import * as React from "react";
import { BrainCircuit, Menu, LogIn, UserPlus } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { pushView } from "@/stores/useAppStore";

const NAV_LINKS = [
  { href: "#services", label: "خدمات" },
  { href: "#courses", label: "دوره‌ها" },
  { href: "#commission", label: "کمیسیون" },
  { href: "#faq", label: "سوالات" },
];

type Settings = {
  siteName?: string | null;
  supportPhone?: string | null;
  heroTitle?: string | null;
  heroSubtitle?: string | null;
  commissionPct?: number | null;
};

const DEFAULT_SITE_NAME = "آکادمی فَران";

function scrollToHash(href: string) {
  if (typeof window === "undefined") return;
  const el = document.querySelector(href);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

export function Navbar() {
  const [scrolled, setScrolled] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [siteName, setSiteName] = React.useState<string>(DEFAULT_SITE_NAME);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  React.useEffect(() => {
    let alive = true;
    fetch("/api/settings", { cache: "no-store" })
      .then((r) => r.json())
      .then((data: { settings?: Settings }) => {
        if (alive && data.settings?.siteName) {
          setSiteName(data.settings.siteName);
        }
      })
      .catch(() => {
        /* fallback to default */
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full transition-all duration-300",
        scrolled
          ? "border-b border-border/60 bg-background/80 backdrop-blur-md supports-[backdrop-filter]:bg-background/60"
          : "border-b border-transparent bg-transparent",
      )}
    >
      <nav
        className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between gap-4 px-4"
        aria-label="ناوبری اصلی"
      >
        {/* Logo */}
        <button
          type="button"
          onClick={() => {
            if (typeof window !== "undefined") {
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
          className="flex items-center gap-2 transition-opacity hover:opacity-90"
          aria-label={`${siteName} — خانه`}
        >
          <span className="flex size-9 items-center justify-center rounded-xl bg-primary/15 text-primary ring-1 ring-primary/30">
            <BrainCircuit className="size-5" />
          </span>
          <span className="text-lg font-bold tracking-tight">{siteName}</span>
        </button>

        {/* Desktop links */}
        <ul className="hidden items-center gap-1 md:flex">
          {NAV_LINKS.map((link) => (
            <li key={link.href}>
              <button
                type="button"
                onClick={() => scrollToHash(link.href)}
                className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                {link.label}
              </button>
            </li>
          ))}
        </ul>

        {/* Desktop actions */}
        <div className="hidden items-center gap-2 md:flex">
          <Button
            variant="ghost"
            onClick={() => pushView("login")}
            className="gap-2"
          >
            <LogIn className="size-4" />
            ورود
          </Button>
          <Button onClick={() => pushView("register")} className="gap-2">
            <UserPlus className="size-4" />
            ثبت‌نام
          </Button>
        </div>

        {/* Mobile menu */}
        <div className="md:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="باز کردن منو">
                <Menu className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <span className="flex size-8 items-center justify-center rounded-lg bg-primary/15 text-primary">
                    <BrainCircuit className="size-4" />
                  </span>
                  {siteName}
                </SheetTitle>
              </SheetHeader>
              <div className="flex flex-col gap-1 px-4">
                {NAV_LINKS.map((link) => (
                  <SheetClose asChild key={link.href}>
                    <button
                      type="button"
                      onClick={() => scrollToHash(link.href)}
                      className="rounded-lg px-3 py-2 text-right text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                    >
                      {link.label}
                    </button>
                  </SheetClose>
                ))}
              </div>
              <div className="mt-auto flex flex-col gap-2 px-4 pb-6">
                <SheetClose asChild>
                  <Button
                    variant="outline"
                    onClick={() => pushView("login")}
                    className="w-full gap-2"
                  >
                    <LogIn className="size-4" />
                    ورود
                  </Button>
                </SheetClose>
                <SheetClose asChild>
                  <Button
                    onClick={() => pushView("register")}
                    className="w-full gap-2"
                  >
                    <UserPlus className="size-4" />
                    ثبت‌نام
                  </Button>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}

export default Navbar;
